import React, { useMemo, useState } from 'react';
import { TaxExpense } from '../../../types/database';
import { formatCurrency, formatMonth } from '../../../lib/format';
import { cn } from '../../../lib/utils';
import { TAX_YEAR_MONTHS, calendarYearForMonth } from '../../../utils/taxUtils';

interface ExpensesByMonthChartProps {
  taxYearStart: number;
  expenses: TaxExpense[];
  selectedMonth: number | null;
  onSelectMonth: (month: number | null) => void;
}

const CHART_HEIGHT = 132;

/** Round the axis max up to a tidy number so gridline labels read cleanly. */
const niceMax = (value: number): number => {
  if (value <= 0) return 100;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const step = [1, 2, 2.5, 5, 10].find((s) => s * magnitude >= value) ?? 10;
  return step * magnitude;
};

/** Monthly totals across the tax year (April → March). Click a bar to filter the list to that month. */
export const ExpensesByMonthChart: React.FC<ExpensesByMonthChartProps> = ({
  taxYearStart,
  expenses,
  selectedMonth,
  onSelectMonth,
}) => {
  const [hovered, setHovered] = useState<number | null>(null);

  const months = useMemo(
    () =>
      TAX_YEAR_MONTHS.map((month) => {
        const items = expenses.filter((e) => e.month === month);
        return {
          month,
          year: calendarYearForMonth(taxYearStart, month),
          total: items.reduce((sum, e) => sum + e.amount, 0),
          count: items.length,
        };
      }),
    [expenses, taxYearStart]
  );

  const max = niceMax(Math.max(...months.map((m) => m.total)));
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const isCurrent = (m: { month: number; year: number }) => m.month === currentMonth && m.year === now.getFullYear();

  return (
    <div>
      <div className="flex gap-2">
        {/* Y axis labels */}
        <div className="numeric flex flex-col justify-between pb-6 text-right text-[11px] text-fg-subtle" style={{ height: CHART_HEIGHT + 24 }}>
          <span>{formatCurrency(max).replace(/\.00$/, '')}</span>
          <span>{formatCurrency(max / 2).replace(/\.00$/, '')}</span>
          <span>£0</span>
        </div>

        <div className="relative min-w-0 flex-1">
          {/* Gridlines */}
          <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 flex flex-col justify-between" style={{ height: CHART_HEIGHT }}>
            <span className="border-t border-dashed border-white/[0.06]" />
            <span className="border-t border-dashed border-white/[0.06]" />
            <span className="border-t border-white/[0.12]" />
          </div>

          <div className="relative grid grid-cols-12 gap-1 sm:gap-1.5">
            {months.map((m, index) => {
              const heightPct = (m.total / max) * 100;
              const dimmed = selectedMonth !== null && selectedMonth !== m.month;
              const selected = selectedMonth === m.month;
              return (
                <button
                  key={m.month}
                  type="button"
                  onClick={() => onSelectMonth(selected ? null : m.month)}
                  onMouseEnter={() => setHovered(m.month)}
                  onMouseLeave={() => setHovered(null)}
                  onFocus={() => setHovered(m.month)}
                  onBlur={() => setHovered(null)}
                  aria-pressed={selected}
                  aria-label={`${formatMonth(m.year, m.month)}: ${formatCurrency(m.total)} across ${m.count} expenses`}
                  className="group relative flex flex-col items-center rounded-md outline-offset-2"
                >
                  {/* Full-height hit target with the bar anchored to the baseline */}
                  <span className="flex w-full items-end justify-center rounded-md transition-colors group-hover:bg-white/[0.03]" style={{ height: CHART_HEIGHT }}>
                    {m.total > 0 && (
                      <span
                        className={cn(
                          'w-full max-w-9 rounded-t-[4px] transition-[opacity,background-color] duration-150',
                          selected ? 'bg-brand-hover' : 'bg-brand',
                          dimmed && 'opacity-30'
                        )}
                        style={{ height: `max(${heightPct}%, 3px)` }}
                      />
                    )}
                  </span>
                  <span
                    className={cn(
                      'mt-1.5 text-[11px] leading-none',
                      selected ? 'font-medium text-fg' : isCurrent(m) ? 'text-brand' : 'text-fg-subtle'
                    )}
                  >
                    <span className="sm:hidden">{formatMonth(m.year, m.month, 'short').charAt(0)}</span>
                    <span className="hidden sm:inline">{formatMonth(m.year, m.month, 'short')}</span>
                  </span>

                  {hovered === m.month && (
                    <span
                      role="tooltip"
                      className={cn(
                        "pointer-events-none absolute bottom-full z-10 mb-1 w-max animate-fade-in rounded-lg border border-line-strong bg-elevated px-2.5 py-1.5 text-left shadow-pop",
                        index < 2 ? "left-0" : index > 9 ? "right-0" : "left-1/2 -translate-x-1/2"
                      )}
                    >
                      <span className="block text-[11px] text-fg-subtle">{formatMonth(m.year, m.month)}</span>
                      <span className="numeric block text-[13px] font-medium text-fg">{formatCurrency(m.total)}</span>
                      <span className="block text-[11px] text-fg-muted">
                        {m.count} {m.count === 1 ? 'expense' : 'expenses'}
                      </span>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
